<template>
  <div>
    <b-row>
      <b-col md="6">
        <h2>{{ $t('Time Limit Settings') }}</h2>
      </b-col>
      <b-col md="6" class="px-0">
        <b-row class="justiy-content-end align-items-center">
          <b-col md="8" cols="7">
            <b-form-group>
              <b-form-input v-model="searchFor" class="float-right" :placeholder="$t('Search')"
                @input="filterMembers" />
            </b-form-group>
          </b-col>

          <b-col md="4" cols="5" class="px-0">
            <b-button v-b-modal.add-user-modal class="ml-2 mb-1" variant="primary">
              {{ $t('Add Member') }}
            </b-button>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- Teams Table -->
    <b-overlay :show="isProcessing">
      <b-table v-if="members.length" :fields="fields" :items="members" :responsive="IS_MOBILE()" show-empty
        :busy="isProcessing" @row-hovered="(_, index) => hoveredRowIndex = index"
        @row-unhovered="() => hoveredRowIndex = null" @row-clicked="item => IS_MOBILE() ? startEditing(item) : ''">
        <template #cell(name)="row">
          <div :style="IS_MOBILE() ? 'width: 50vw;' : ''">
            <user-with-tags :user="row.item" />
          </div>
        </template>

        <template #cell(hours)="row">
          <!-- View Mode -->
          <div v-if="!row.item.isEditing" class="d-inline-flex align-items-center time-limit-display">
            <div :class="{ 'editable-text': hoveredRowIndex === row.index && canEdit }"
              @click="canEdit ? startEditing(row.item) : ''">
              <span v-if="row.item.hours">
                {{ row.item.hours }} hrs / {{ row.item.limitType === 'WEEKLY' ? 'Week' : 'Month' }}
              </span>
              <span v-else class="text-muted">No Limit</span>
            </div>
            <feather-icon v-if="hoveredRowIndex === row.index && canEdit" icon="EditIcon" size="16"
              class="ml-2 cursor-pointer text-primary" @click="startEditing(row.item)" />
          </div>

          <!-- Edit Mode -->
          <div v-else :class="IS_MOBILE() ? '' : 'd-flex align-items-center flex-nowrap'">
            <div class="d-flex" :class="{ 'mb-1': IS_MOBILE() }">
              <validation-observer ref="limitForm">
                <validation-provider name="limit hour" rules="min_value:1" #default="{ errors }">
                  <b-form-input v-model="row.item.editHours" type="number" size="sm" placeholder="Hours" class="mr-1"
                    autocomplete="off" style="width: 70px;" :min="1" @keyup.esc="cancelEdit(row.item)"
                    @keyup.enter="saveEdit(row.item)" />
                  <span v-if="errors[0]" class="text-danger small">{{ errors[0] }}</span>
                </validation-provider>
              </validation-observer>

              <v-select v-model="row.item.editType" :reduce="limit => limit.value" :clearable="false"
                :options="limitForOptions" :append-to-body="true" :calculate-position="withPopper"
                class="mr-1 limit-select" style="width: 95px; min-width: 95px;"
                @option:selected="option => { row.item.editType = option.value }" />
            </div>

            <div class="d-flex">
              <b-button variant="success" size="sm" class="mr-1 p-1 btn-icon-sm"
                :disabled="isProcessing || !row.item.editHours || row.item.editHours <= 0" @click="saveEdit(row.item)">
                <feather-icon icon="CheckIcon" size="14" />
              </b-button>

              <b-button variant="secondary" size="sm" class="mr-1 p-1 btn-icon-sm" @click="cancelEdit(row.item)">
                <feather-icon icon="XIcon" size="14" />
              </b-button>

              <b-button v-if="row.item.hours" variant="outline-danger" size="sm" class="p-1 btn-icon-sm"
                :disabled="isProcessing" @click="confirmDelete(row.item)">
                <feather-icon icon="Trash2Icon" size="14" />
              </b-button>
            </div>
          </div>
        </template>
      </b-table>
    </b-overlay>

    <!-- Time Limit Delete Modal -->
    <b-modal id="time-limit-delete-modal"
      :title="selectedUser ? `Remove Time Limit for ${selectedUser.name}` : 'Remove Time Limit'" ok-title="Remove"
      ok-variant="danger" @ok="deleteTimeLimit">
      <span v-if="selectedUser">Are you sure you want to remove time limit for {{ selectedUser.name }}?</span>
    </b-modal>

    <confirm-modal v-if="selectedUser" modal="confirm-change-modal" title="Discard Changes?"
      :description="`All the changes made to ${selectedUser.name}'s time limit will be lost. Do you want to proceed?`"
      ok-title="Confirm" ok-variant="danger" @confirm="onConfirm" />

    <!-- Add member -->
    <add-team :team-info="currentTeam" :members="currentTeam.users?.data || []"
      @updateTeamCount="() => $emit('updated')" />
  </div>
</template>

<script setup>
import {
  BRow, BCol, BFormGroup, BFormInput, BTable, BButton, BModal, BOverlay,
} from 'bootstrap-vue'
import useApollo from '@/plugins/graphql/useApollo'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { checkPermission } from '@/utils/permissions'
import addTeam from '../CRUD/components/addTeam.vue'
import { ref, watch, onMounted, getCurrentInstance } from 'vue'
import UserWithTags from '@/views/common/components/UserWithTags.vue'
import vSelect from 'vue-select'
import ConfirmModal from '@/views/Wallet/components/reusables/ConfirmModal.vue'
import { createPopper } from '@popperjs/core'

const root = getCurrentInstance().proxy.$root

const props = defineProps({
  teams: {
    type: [Object, Array],
    default: () => { },
  },
  currentTeam: {
    type: Object,
    default: () => { },
  },
})

const emit = defineEmits(['updated'])

const limitForOptions = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
]

const fields = [
  { label: root.$t('Team Member'), key: 'name', thClass: 'text-capitalize w-50' },
  { label: root.$t('Time Limit'), key: 'hours', thClass: 'text-capitalize w-50' },
]

const searchFor = ref('')
const selectedProject = ref(root.$store.state.project.selectedProject)
const isProcessing = ref(false)
const canEdit = ref(false)
const hoveredRowIndex = ref(null)
const selectedUser = ref(null)
const tempUser = ref(null)
const limitForm = ref(null)
const members = ref([])

const withPopper = (dropdownList, component, { width }) => {
  dropdownList.style.width = width
  const popper = createPopper(component.$refs.toggle, dropdownList, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, -1] },
      },
      {
        name: 'toggleClass',
        enabled: true,
        phase: 'write',
        fn({ state }) {
          component.$el.classList.toggle('drop-up', state.placement === 'top')
        },
      },
    ],
  })
  return () => popper.destroy()
}

const normalizeMembers = (list) => (Array.isArray(list) ? list : [])
  .map(user => ({
    ...user,
    hours: user.hours ?? null,
    limitType: user.limitType ?? null,
    isEditing: false,
    editHours: null,
    editType: null,
  }))

const filterMembers = () => {
  const all = normalizeMembers(props.teams)
  const key = (searchFor.value || '').trim().toLowerCase()
  members.value = key ? all.filter(u => (u.name || '').toLowerCase().includes(key)) : all
}

watch(() => props.teams, () => {
  filterMembers()
}, { immediate: true })

onMounted(() => {
  checkPermission('timing.limit', permissions => {
    canEdit.value = permissions['timing.limit'] === true
  })
})

const onConfirm = () => {
  cancelEdit(selectedUser.value)
  selectedUser.value = null
  startEditing(tempUser.value)
}

const hasUnsavedChanges = (user) => {
  if (!user?.isEditing) return false

  const originalHours = user.hours ?? ''
  const editedHours = user.editHours ?? ''
  const hoursChanged = String(editedHours) !== String(originalHours)

  const originalType = user.limitType ?? ''
  const editedType = user.editType ?? ''
  const typeChanged = String(editedType) !== String(originalType)

  return hoursChanged || typeChanged
}

const startEditing = (item) => {
  if (!canEdit.value) return

  // Clicking within the same row (especially on mobile) shouldn't trigger confirm logic.
  if (selectedUser.value === item && item?.isEditing) return

  if (selectedUser.value && selectedUser.value !== item) {
    if (hasUnsavedChanges(selectedUser.value)) {
      tempUser.value = item
      root.$bvModal.show('confirm-change-modal')
      return
    }
    cancelEdit(selectedUser.value)
  }

  selectedUser.value = item
  tempUser.value = null
  item.isEditing = true
  item.editHours = item.hours || ''
  item.editType = item.limitType || 'WEEKLY'
}

const cancelEdit = (item) => {
  if (!item) return
  item.isEditing = false
  item.editHours = null
  item.editType = null

  if (selectedUser.value === item) {
    selectedUser.value = null
  }
}

const confirmDelete = (item) => {
  selectedUser.value = item
  root.$bvModal.show('time-limit-delete-modal')
}

const saveEdit = (item) => {
  if (!item.editHours || item.editHours <= 0) {
    root.showErrorMessage({ data: { message: 'Please enter a valid number of hours (minimum 1)' } })
    return
  }

  isProcessing.value = true
  useApollo.team.updateTeamMemberLimit({
    userUid: item.uid,
    projectUid: selectedProject.value,
    hours: parseInt(item.editHours, 10),
    type: item.editType,
  }).then(res => {
    item.hours = parseInt(item.editHours, 10)
    item.limitType = item.editType
    item.isEditing = false
    selectedUser.value = null
    emit('updated')
    root.showSuccess(res.data.updateUserProjectTimeLimit.message)
  }).catch(error => {
    root.showErrorMessage(error)
  }).finally(() => {
    isProcessing.value = false
  })
}

const deleteTimeLimit = () => {
  isProcessing.value = true
  useApollo.team.updateTeamMemberLimit({
    userUid: selectedUser.value.uid,
    projectUid: selectedProject.value,
    hours: 0,
    type: selectedUser.value.limitType ?? 'WEEKLY',
  }).then(res => {
    selectedUser.value.hours = null
    selectedUser.value.limitType = null
    selectedUser.value.isEditing = false
    limitForm.value?.reset()
    emit('updated')
    root.showSuccess(res.data.updateUserProjectTimeLimit.message)
  }).catch(error => {
    root.showErrorMessage(error)
  }).finally(() => {
    isProcessing.value = false
    selectedUser.value = null
  })
}
</script>

<style scoped>
.icon-btn {
  padding: 10px;
}

.time-limit-display {
  min-height: 38px;
}

.editable-text {
  border-bottom: 1px dashed #7367f0;
  cursor: pointer;
  padding-bottom: 2px;
}

.btn-icon-sm {
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.limit-select>>>.vs__dropdown-toggle {
  min-height: 28px;
  height: 28px;
  padding: 0 0 0 8px;
}

.limit-select>>>.vs__selected {
  font-size: 13px;
  margin: 2px 2px 2px 0;
  padding: 0 2px;
}

.limit-select>>>.vs__actions {
  padding: 2px 4px 0 0;
}

.limit-select>>>.vs__search {
  margin: 0;
  padding: 0;
}
</style>
